import { useEffect, useRef, useState, useCallback } from "react";
import {
  X,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  ArrowRight,
  Sparkles,
  ListChecks,
  ShieldCheck,
  Coins,
  Bot,
  Compass,
  Target,
} from "lucide-react";

/* ---------- Scenes ----------
   Each scene is ~7s and renders an animated visual + voice-over caption.
   Total runtime ≈ 56s. Designed to look like a real explainer video. */
const SCENES = [
  {
    key: "open",
    eyebrow: "A film about starting up",
    headline: "Three Little Birds",
    sub: "An operating system for first-time founders.",
    voice: "",
    accent: "#7C5FB0",
    bg: "#000",
    visual: "features_video",
    duration: 24,
    fadeCaptionAfter: 4,
  },
  {
    key: "wizard",
    eyebrow: "Step 01",
    headline: "Tell us about your idea.",
    sub: "A 5-step wizard. 10 minutes. Zero jargon.",
    voice: "Answer a five-step questionnaire. We translate raw thought into a strategist-grade plan.",
    accent: "#C4B0E6",
    bg: "#0a0a0a",
    visual: "screenshot",
    img: "01-wizard.jpg",
    duration: 4,
  },
  {
    key: "plan",
    eyebrow: "Step 02",
    headline: "Get a strategist-grade plan.",
    sub: "Pricing, projections, GTM, competition.",
    voice: "Our drafts pricing, three-year projections, customer acquisition, and competition — tailored to you.",
    accent: "#C4B0E6",
    bg: "#0a0a0a",
    visual: "screenshot",
    img: "02-plan-top.jpg",
    duration: 4,
  },
  {
    key: "forecast",
    eyebrow: "Step 03",
    headline: "A forecast that drives itself.",
    sub: "Revenue, costs and headcount — linked.",
    voice: "A driver-based forecast links revenue, costs and hiring to your live actuals, so projections update themselves.",
    accent: "#C4B0E6",
    bg: "#0a0a0a",
    visual: "screenshot",
    img: "12-forecast.jpg",
    duration: 4,
  },
  {
    key: "benchmarks",
    eyebrow: "Step 04",
    headline: "Measured against your industry.",
    sub: "Margins, costs and ratios vs. real peers.",
    voice: "See your margins, costs and ratios benchmarked against real businesses in your industry — instantly.",
    accent: "#C4B0E6",
    bg: "#0a0a0a",
    visual: "screenshot",
    img: "13-benchmarks.jpg",
    duration: 4,
  },
  {
    key: "performance",
    eyebrow: "Step 05",
    headline: "Plan versus actual, every month.",
    sub: "Track the variance. Catch drift early.",
    voice: "A performance dashboard compares your plan against actuals every month, flagging variance before it hurts.",
    accent: "#C4B0E6",
    bg: "#0a0a0a",
    visual: "screenshot",
    img: "14-performance.jpg",
    duration: 4,
  },
  {
    key: "competitor",
    eyebrow: "Step 06",
    headline: "Everything the big tools do.",
    sub: "And the parts they leave out.",
    voice: "We match the leading planning tools feature for feature — then go further on execution and live guidance.",
    accent: "#C4B0E6",
    bg: "#0a0a0a",
    visual: "screenshot",
    img: "15-competitor.jpg",
    duration: 4,
  },
  {
    key: "samples",
    eyebrow: "Step 07",
    headline: "Fifty plans to learn from.",
    sub: "Real examples across every industry.",
    voice: "Browse fifty complete sample plans across every industry — a head start you can borrow from on day one.",
    accent: "#C4B0E6",
    bg: "#0a0a0a",
    visual: "screenshot",
    img: "16-samples.jpg",
    duration: 4,
  },
  {
    key: "roadmap",
    eyebrow: "Step 08",
    headline: "A roadmap with service agents.",
    sub: "Phased tasks on a drag-and-drop launch board — agents that do the work.",
    voice: "Twelve to sixteen phased tasks land on a launch board, each with a service agent ready to file your LLC, set up Stripe, or hire your first contractor.",
    accent: "#C4B0E6",
    bg: "#0a0a0a",
    visual: "screenshot",
    img: "08-roadmap.jpg",
    duration: 4,
  },
  {
    key: "gantt",
    eyebrow: "Step 09",
    headline: "Timeline & dependencies.",
    sub: "A Gantt view with the critical path.",
    voice: "Switch to a Gantt timeline to see dependencies, the critical path, and exactly what unblocks what.",
    accent: "#C4B0E6",
    bg: "#0a0a0a",
    visual: "screenshot",
    img: "10-gantt.jpg",
    duration: 4,
  },
  {
    key: "mentor",
    eyebrow: "Step 10",
    headline: "A Three Little Birds co-founder, on call.",
    sub: "Mentor + service agents on every task.",
    voice: "An Founder Mentor knows every plan you have — and tells you exactly what to prioritize across all your businesses, this week.",
    accent: "#7C5FB0",
    bg: "linear-gradient(135deg,#0a0a0a 0%,#260f00 100%)",
    visual: "mentor",
    duration: 4,
  },
  {
    key: "testimonial",
    eyebrow: "A founder's story",
    headline: "\u201cI launched in six weeks, not six months.\u201d",
    sub: "Aisha J. \u00b7 founder, hospitality startup",
    voice: "With Three Little Birds, I launched in six weeks. Not six months. For the first time, I knew exactly what to do next.",
    accent: "#7C5FB0",
    bg: "#000",
    visual: "testimonial_video",
    duration: 8,
  },
  {
    key: "cta",
    eyebrow: "Three little birds",
    headline: "Don't worry about a thing.",
    sub: "Sign in with Google. Spin up your first plan in fifteen minutes.",
    voice: "Three little birds. Don't worry about a thing — every little thing is gonna be alright.",
    accent: "#7C5FB0",
    bg: "linear-gradient(135deg,#0a0a0a 0%,#000 100%)",
    visual: "cta",
    duration: 7,
    cta: true,
  },
];

const TOTAL = SCENES.reduce((a, s) => a + s.duration, 0);

function fmt(secs) {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function sceneAt(t) {
  let acc = 0;
  for (let i = 0; i < SCENES.length; i++) {
    if (t < acc + SCENES[i].duration) {
      return { idx: i, scene: SCENES[i], offset: t - acc };
    }
    acc += SCENES[i].duration;
  }
  return { idx: SCENES.length - 1, scene: SCENES[SCENES.length - 1], offset: SCENES[SCENES.length - 1].duration };
}

export default function FeatureVideo({ open, onClose, onPrimaryCta, primaryCtaLabel, startUnmuted = false }) {
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [viewersLast7d, setViewersLast7d] = useState(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastTickRef = useRef(null);
  const lastSceneIdxRef = useRef(0);
  const completedFiredRef = useRef(false);

  // Fire-and-forget analytics beacon. No auth, no error handling — failures
  // must not block playback. Uses a relative URL so the same origin as the
  // SPA routes through to the backend ingress.
  const fireEvent = useCallback((event, sceneIdx, sceneKey, timeS) => {
    try {
      fetch("/api/public/feature-video/event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          event,
          scene_idx: sceneIdx,
          scene_key: sceneKey,
          time_s: Number.isFinite(timeS) ? Math.round(timeS * 10) / 10 : 0,
        }),
      }).catch(() => {});
    } catch {
      /* noop */
    }
  }, []);

  // Reset when (re)opened — also fire `started` and load viewer count.
  useEffect(() => {
    if (open) {
      setTime(0);
      setPlaying(true);
      // When the viewer arrives via the IntroStory hand-off they already opted
      // into the cinematic experience — start with sound on. Direct opens
      // (e.g. a "Play" button) stay muted. If the browser blocks unmuted
      // autoplay, InlineVideo falls back to muted (see onAudioBlocked).
      setMuted(!startUnmuted);
      lastSceneIdxRef.current = 0;
      completedFiredRef.current = false;
      const first = SCENES[0];
      fireEvent("started", 0, first.key, 0);
      fetch("/api/public/feature-video/stats")
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d && typeof d.viewers_last_7d === "number") {
            setViewersLast7d(d.viewers_last_7d);
          }
        })
        .catch(() => {});
    }
  }, [open, fireEvent, startUnmuted]);

  // RAF time loop
  useEffect(() => {
    if (!open || !playing) {
      cancelAnimationFrame(rafRef.current);
      lastTickRef.current = null;
      return undefined;
    }
    const tick = (ts) => {
      if (lastTickRef.current == null) lastTickRef.current = ts;
      const dt = (ts - lastTickRef.current) / 1000;
      lastTickRef.current = ts;
      setTime((t) => {
        const n = t + dt;
        // Detect scene crossings and fire `scene_end` for each scene completed.
        const prevIdx = sceneAt(t).idx;
        const currIdx = sceneAt(Math.min(TOTAL, n)).idx;
        if (currIdx > prevIdx) {
          // We just left scene prevIdx → it ended.
          fireEvent("scene_end", prevIdx, SCENES[prevIdx].key, n);
          lastSceneIdxRef.current = currIdx;
        }
        if (n >= TOTAL) {
          if (!completedFiredRef.current) {
            // Final scene's scene_end + the completed marker.
            fireEvent(
              "scene_end",
              SCENES.length - 1,
              SCENES[SCENES.length - 1].key,
              TOTAL,
            );
            fireEvent("completed", SCENES.length - 1, SCENES[SCENES.length - 1].key, TOTAL);
            completedFiredRef.current = true;
          }
          setPlaying(false);
          return TOTAL;
        }
        return n;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [open, playing]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: `fireEvent` is read inside `tick` via closure but is
  // stable for the lifetime of the FeatureVideo component (defined via
  // useCallback above). Adding it to deps would force the RAF loop to
  // restart on every render — visible as flicker in the progress bar.

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
      else if (e.key === " ") {
        e.preventDefault();
        setPlaying((p) => !p);
      } else if (e.key === "m") setMuted((m) => !m);
      else if (e.key === "f") toggleFullscreen();
      else if (e.key === "ArrowRight") setTime((t) => Math.min(TOTAL, t + 5));
      else if (e.key === "ArrowLeft") setTime((t) => Math.max(0, t - 5));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleFullscreen = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const handleClose = useCallback(() => {
    if (!completedFiredRef.current) {
      const { idx, scene } = sceneAt(time);
      fireEvent("closed", idx, scene.key, time);
    }
    onClose?.();
  }, [time, fireEvent, onClose]);

  if (!open) return null;

  const { scene, idx, offset } = sceneAt(time);
  const sceneProgress = Math.min(1, offset / scene.duration);
  const ended = time >= TOTAL;

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setTime(ratio * TOTAL);
    setPlaying(true);
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/95 p-0 sm:p-6"
      data-testid="feature-video-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Feature video"
    >
      <div
        ref={wrapRef}
        className="relative w-full h-full sm:h-[min(90vh,720px)] sm:max-w-5xl bg-black overflow-hidden border-2 border-neutral-800 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
        data-testid="feature-video-frame"
        style={{ aspectRatio: fullscreen ? "auto" : undefined }}
      >
        {/* ===== Scene stage ===== */}
        <div
          key={scene.key}
          className="absolute inset-0 transition-[background] duration-500"
          style={{ background: scene.bg }}
        >
          {/* Subtle film grain */}
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' /></svg>\")",
            }}
          />
          {/* Diagonal accent */}
          <div
            className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-[140px] opacity-40 pointer-events-none transition-colors duration-500"
            style={{ backgroundColor: scene.accent }}
          />

          {/* Visual layer (per-scene) */}
          <div className="absolute inset-0 flex items-center justify-center">
            <SceneVisual
              scene={scene}
              t={sceneProgress}
              offset={offset}
              playing={playing}
              muted={muted}
              onAudioBlocked={() => setMuted(true)}
            />
          </div>

          {/* Caption / lower-third */}
          <div
            className={`absolute left-0 right-0 bottom-24 sm:bottom-28 px-6 sm:px-12 transition-opacity duration-700 ${
              scene.light ? "text-neutral-900" : "text-white"
            }`}
            style={{
              opacity:
                scene.fadeCaptionAfter != null && offset > scene.fadeCaptionAfter
                  ? 0
                  : 1,
              pointerEvents:
                scene.fadeCaptionAfter != null && offset > scene.fadeCaptionAfter
                  ? "none"
                  : undefined,
            }}
          >
            <div
              className="max-w-3xl"
              style={{
                animation: "vidCaptionIn 700ms cubic-bezier(.2,.8,.2,1) both",
              }}
              key={`cap-${idx}`}
              data-testid={`feature-video-caption-${idx}`}
            >
              <div
                className="text-[10px] sm:text-xs uppercase tracking-[0.4em] font-bold mb-3"
                style={{ color: scene.accent }}
              >
                {scene.eyebrow}
              </div>
              <h2 className="font-display font-black tracking-tighter leading-[0.95] text-3xl sm:text-5xl lg:text-6xl">
                {scene.headline}
              </h2>
              {scene.sub && (
                <p
                  className={`mt-3 sm:mt-4 text-sm sm:text-lg max-w-xl ${
                    scene.light ? "text-neutral-700" : "text-neutral-300"
                  }`}
                >
                  {scene.sub}
                </p>
              )}
              {scene.cta && (
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => onPrimaryCta?.()}
                    data-testid="feature-video-cta-btn"
                    className="h-12 px-6 inline-flex items-center gap-2 bg-[#7C5FB0] hover:bg-[#5E4689] text-white font-bold tracking-wide group"
                  >
                    {primaryCtaLabel || "Sign in & start your plan"}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTime(0);
                      setPlaying(true);
                    }}
                    data-testid="feature-video-replay-btn"
                    className="h-12 px-5 inline-flex items-center gap-2 border-2 border-white/40 hover:bg-white hover:text-neutral-900 text-white text-xs uppercase tracking-[0.2em] font-bold"
                  >
                    Replay
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* "Closed-caption" voice-over (since the video is muted) */}
          {!muted && scene.voice && (
            <div
              className="absolute left-0 right-0 bottom-20 px-6 sm:px-12 flex justify-center"
              key={`vo-${idx}`}
            >
              <div className="bg-black/70 text-white text-xs sm:text-sm px-3 py-1.5 max-w-2xl text-center backdrop-blur">
                {scene.voice}
              </div>
            </div>
          )}
        </div>

        {/* ===== Top bar ===== */}
        <div className="absolute top-0 left-0 right-0 px-4 sm:px-6 py-4 flex items-center justify-between z-20 bg-gradient-to-b from-black/60 to-transparent">
          <div className="flex items-center gap-2 text-white">
            <img src="/logo-mark.png" alt="Three Little Birds" className="h-7 w-auto" draggable={false} />
            <div className="font-display font-black text-sm tracking-tighter">
              THREE LITTLE BIRDS · feature film
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            data-testid="feature-video-close-btn"
            aria-label="Close video"
            className="w-9 h-9 flex items-center justify-center border border-white/30 hover:border-white text-white hover:bg-white hover:text-neutral-900 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ===== Player controls ===== */}
        <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 py-4 z-20 bg-gradient-to-t from-black/85 to-transparent">
          {/* Viewer count overlay — social proof above the scrubber */}
          {viewersLast7d != null && viewersLast7d >= 5 && (
            <div
              className="mb-3 inline-flex items-center gap-2 bg-[#7C5FB0]/90 text-white text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 backdrop-blur"
              data-testid="feature-video-viewer-count"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              <span className="font-bold">
                {viewersLast7d.toLocaleString()} founders
              </span>
              <span className="opacity-80">watched this week</span>
            </div>
          )}

          {/* Scrubber */}
          <button
            type="button"
            onClick={handleSeek}
            data-testid="feature-video-scrubber"
            className="relative block w-full h-1.5 bg-white/15 hover:h-2 transition-all cursor-pointer"
            aria-label="Seek"
          >
            <div
              className="absolute top-0 left-0 h-full bg-[#7C5FB0]"
              style={{ width: `${(time / TOTAL) * 100}%` }}
            />
            {/* chapter markers */}
            {(() => {
              let acc = 0;
              return SCENES.map((s, i) => {
                const left = (acc / TOTAL) * 100;
                acc += s.duration;
                return (
                  <div
                    key={s.key}
                    className="absolute top-0 h-full w-px bg-white/40"
                    style={{ left: `${left}%` }}
                    aria-hidden
                    data-chapter={i}
                  />
                );
              });
            })()}
          </button>

          <div className="mt-3 flex items-center gap-3 text-white">
            <button
              type="button"
              onClick={() => {
                if (ended) {
                  setTime(0);
                  setPlaying(true);
                } else {
                  setPlaying((p) => !p);
                }
              }}
              data-testid="feature-video-play-btn"
              aria-label={playing ? "Pause" : "Play"}
              className="w-10 h-10 flex items-center justify-center bg-[#7C5FB0] hover:bg-[#5E4689] text-white"
            >
              {playing && !ended ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>

            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              data-testid="feature-video-mute-btn"
              aria-label={muted ? "Unmute (show captions)" : "Mute"}
              className="w-9 h-9 flex items-center justify-center border border-white/30 hover:border-white"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>

            <div className="text-xs tabular-nums tracking-wide opacity-90">
              {fmt(time)} / {fmt(TOTAL)}
            </div>

            <div className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-white/60 ml-2 truncate">
              {String(idx + 1).padStart(2, "0")} · {scene.headline.replace(/\n/g, " ")}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <span className="hidden md:inline text-[10px] uppercase tracking-[0.25em] text-white/50">
                Space · M · F
              </span>
              <button
                type="button"
                onClick={toggleFullscreen}
                data-testid="feature-video-fullscreen-btn"
                aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                className="w-9 h-9 flex items-center justify-center border border-white/30 hover:border-white"
              >
                {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes vidCaptionIn {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes vidPulse {
          0%,100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.04); opacity: .85; }
        }
        @keyframes vidSlideIn {
          from { opacity: 0; transform: translateX(-12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes vidFloat {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes vidScan {
          0% { transform: translateX(-110%); }
          100% { transform: translateX(110%); }
        }
        @keyframes vidGrow {
          from { transform: scaleY(0.05); }
          to { transform: scaleY(1); }
        }
        @keyframes vidKenburns {
          from { transform: scale(1.04) translateY(0); }
          to { transform: scale(1.13) translateY(-8%); }
        }
      `}</style>
    </div>
  );
}

/* ============ Scene visuals (CSS-animated, scene-keyed) ============ */
function SceneVisual({ scene, t, offset, playing, muted, onAudioBlocked }) {
  switch (scene.visual) {
    case "screenshot":
      return (
        <div
          className="absolute inset-0 w-full h-full overflow-hidden"
          key={`vis-shot-${scene.key}`}
          data-testid="feature-video-screenshot-wrap"
        >
          <img
            src={`${process.env.PUBLIC_URL || ""}/demo/${scene.img}`}
            alt={scene.headline}
            className="absolute inset-0 w-full h-full object-cover object-top"
            style={{
              animation: `vidKenburns ${scene.duration + 1}s linear both`,
              transformOrigin: "top center",
            }}
            draggable={false}
            data-testid="feature-video-screenshot"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"
          />
        </div>
      );
    case "score": {
      const score = Math.round(72 + t * 16); // animates 72 → 88
      const grade = score >= 90 ? "A" : score >= 80 ? "B" : score >= 70 ? "C" : "D";
      const dims = [
        { Icon: Sparkles, label: "Plan health", v: 88 },
        { Icon: ShieldCheck, label: "Compliance", v: 78 },
        { Icon: ListChecks, label: "Roadmap", v: 64 },
        { Icon: Coins, label: "Runway", v: 92 },
        { Icon: Target, label: "Goals", v: 70 },
      ];
      return (
        <div className="w-full max-w-3xl px-8 grid grid-cols-1 sm:grid-cols-5 gap-4" key="vis-score">
          <div className="sm:col-span-2 bg-white border border-neutral-900 p-6 text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Launch-readiness</div>
            <div
              className="font-display font-black text-7xl tracking-tighter text-[#4A4458] mt-2"
              style={{ animation: "vidPulse 2.6s ease-in-out infinite" }}
            >
              {score}
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1">
              Grade · <span className="font-bold text-neutral-900">{grade}</span>
            </div>
          </div>
          <div className="sm:col-span-3 bg-white border border-neutral-900 p-4 space-y-2">
            {dims.map((d, i) => (
              <div key={d.label} className="flex items-center gap-3" style={{ animation: `vidSlideIn 600ms ease-out ${i * 90}ms both` }}>
                <d.Icon className="w-3.5 h-3.5 text-[#4A4458] shrink-0" />
                <div className="text-xs font-bold w-24 shrink-0 text-neutral-900">{d.label}</div>
                <div className="h-1.5 flex-1 bg-neutral-200">
                  <div
                    className="h-1.5 bg-[#7C5FB0] origin-left"
                    style={{
                      width: `${d.v}%`,
                      animation: `vidGrow 900ms ease-out ${i * 120}ms both`,
                    }}
                  />
                </div>
                <div className="text-xs font-bold tabular-nums text-neutral-900">{d.v}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "mentor":
      return (
        <div className="w-full max-w-2xl px-8" key="vis-mentor">
          <div className="bg-neutral-900 border border-white/10 p-5">
            <div className="flex items-center gap-2 text-white">
              <span className="w-7 h-7 flex items-center justify-center bg-[#7C5FB0]">
                <Bot className="w-4 h-4" />
              </span>
              <div className="font-display font-black tracking-tighter text-lg">Founder Mentor</div>
              <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-white/50">live · 03 plans</span>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="bg-white/10 text-white p-2.5 max-w-[80%]" style={{ animation: "vidSlideIn 600ms ease-out 100ms both" }}>
                What should I prioritize this week across all my businesses?
              </div>
              <div
                className="bg-[#7C5FB0] text-white p-2.5 max-w-[85%] ml-auto"
                style={{ animation: "vidSlideIn 600ms ease-out 700ms both" }}
              >
                File the FinCEN BOI report (due Fri) · launch your pricing test · book the CPA you saved last week.
              </div>
              <div className="bg-white/10 text-white p-2.5 max-w-[60%]" style={{ animation: "vidSlideIn 600ms ease-out 1500ms both" }}>
                <Compass className="w-3 h-3 inline mr-1.5 opacity-70" />
                Draft the CPA email for me?
              </div>
            </div>
          </div>
        </div>
      );
    case "cta":
      return (
        <div className="text-center px-8 select-none flex items-end justify-center gap-6" key="vis-cta">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-20 h-20 sm:w-28 sm:h-28 bg-[#7C5FB0]"
              style={{ animation: `vidFloat 2.4s ease-in-out ${i * 0.25}s infinite` }}
            />
          ))}
        </div>
      );
    case "features_video":
      return (
        <InlineVideo
          offset={offset}
          playing={playing}
          muted={muted}
          duration={scene.duration}
          srcMp4="/features-aisha.mp4?v=3"
          srcWebm="/features-aisha.webm?v=3"
          poster="/features-aisha-poster.jpg?v=1"
          testid="feature-video-features-player"
          onAudioBlocked={onAudioBlocked}
        />
      );
    case "testimonial_video":
      return (
        <InlineVideo
          offset={offset}
          playing={playing}
          muted={muted}
          duration={scene.duration}
          srcMp4="/testimonial-aisha.mp4?v=4"
          srcWebm="/testimonial-aisha.webm?v=4"
          poster="/testimonial-aisha-poster.jpg?v=1"
          testid="feature-video-testimonial-player"
          onAudioBlocked={onAudioBlocked}
        />
      );
    default:
      return null;
  }
}

/* ---- Inline video (Sora 2 MP4/WebM, synced to modal scrubber) ---- */
function InlineVideo({ offset, playing, muted, duration, srcMp4, srcWebm, poster, testid, onAudioBlocked }) {
  const vidRef = useRef(null);
  const lastOffsetRef = useRef(offset);
  const [ready, setReady] = useState(false);
  const [blobSrc, setBlobSrc] = useState(null);

  // Production CDN bug workaround: the static asset host serves .mp4/.webm
  // with Content-Type: application/octet-stream, which causes browsers to
  // refuse playback regardless of the <source type="..."> hint. We HEAD-probe
  // the URL; if it isn't already video/*, we fetch the full bytes, re-type
  // them client-side to video/mp4, and swap to a blob: URL. On the preview
  // environment (correct MIME) this branch is a no-op and native streaming
  // is preserved.
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const head = await fetch(srcMp4, { method: "HEAD" });
        const ct = (head.headers.get("content-type") || "").toLowerCase();
        if (ct.startsWith("video/")) return; // browser will play natively
      } catch {
        /* fall through to blob fallback */
      }
      if (cancelled) return;
      try {
        const r = await fetch(srcMp4);
        if (!r.ok) return;
        const blob = await r.blob();
        if (cancelled) return;
        const typed = blob.type && blob.type.startsWith("video/")
          ? blob
          : new Blob([blob], { type: "video/mp4" });
        objectUrl = URL.createObjectURL(typed);
        if (!cancelled) setBlobSrc(objectUrl);
      } catch {
        /* noop — keep <source> tags as final fallback */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [srcMp4]);

  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    // Gate playback on the clip's REAL duration (a scene can be longer than the
    // clip). Once the clip has played through, don't call play() again — doing
    // so on an ended <video> restarts it from 0, which looked like the opening
    // clip "looping" inside its scene. Hold on the last frame instead.
    const realDur = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : duration;
    if (playing && offset < realDur - 0.1 && !v.ended) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [playing, offset, duration]);

  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    v.muted = muted;
    if (!muted) v.volume = 0.9;
  }, [muted]);

  // Always start the segment from the very beginning when it mounts (i.e. when
  // the film opens or re-opens on this scene). Without this, a reused <video>
  // element could retain a prior ended/seeked position and the opening demo
  // would appear skipped. Guarantees the demo plays first, every time.
  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    try { v.currentTime = 0; } catch { /* noop */ }
    v.play().catch(() => {
      // Unmuted autoplay can be blocked by the browser's autoplay policy. Fall
      // back to muted so the clip still rolls, and tell the parent to reflect
      // the muted state (so the unmute control shows the truth).
      if (!v.muted) {
        try { v.muted = true; v.play().catch(() => {}); } catch { /* noop */ }
        onAudioBlocked?.();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const v = vidRef.current;
    if (!v || !ready) return;
    // Only re-seek on a DISCONTINUITY in the timeline (a user scrub / arrow-key
    // skip), never on the gradual sub-frame drift between the RAF clock and the
    // clip's native playback. That continuous correction yanked currentTime
    // every ~second, which re-buffered the audio and produced the
    // stutter/repeat exactly where the speaker pauses mid-clip.
    const prev = lastOffsetRef.current;
    lastOffsetRef.current = offset;
    const realDur = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : duration;
    const target = Math.max(0, Math.min(offset, realDur - 0.05));
    if (Math.abs(offset - prev) > 1.2) {
      try { v.currentTime = target; } catch { /* noop */ }
    }
  }, [offset, ready, duration]);

  return (
    <div
      className="relative w-full h-full flex items-center justify-center bg-black"
      data-testid={`scene-${testid}-wrap`}
    >
      <video
        ref={vidRef}
        className="w-full h-full object-cover"
        preload="auto"
        playsInline
        autoPlay
        muted={muted}
        poster={poster}
        onLoadedData={() => setReady(true)}
        onError={() => setReady(false)}
        data-testid={testid}
        {...(blobSrc ? { src: blobSrc, key: blobSrc } : {})}
      >
        {!blobSrc && srcWebm && <source src={srcWebm} type="video/webm" />}
        {!blobSrc && <source src={srcMp4} type="video/mp4" />}
      </video>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent"
      />
    </div>
  );
}
